import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppModeProvider } from './contexts/AppModeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AlertProvider } from './contexts/AlertContext';
import IntelliSpaceDashboard from './pages/IntelliSpaceDashboard';
import Settings from './pages/Settings';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import { authService } from './services/authService';
import { logger } from './utils/logger';
import SessionExpiredModal from './components/SessionExpiredModal';
import {
  migrateAllFromLocalStorage,
  loadPreferencesToLocalStorage,
  cleanupOrphanedImportedDevices
} from './services/userPreferencesApi';
// Only load test utilities in development environment
if (process.env.NODE_ENV === 'development') {
  // Uncomment these imports when needed for debugging
  // import('./utils/clearImportedDevices'); // 🧹 Clear all imported devices (run once, then comment out)
  // import('./utils/compareImportedDevices'); // 🔍 Compare Firestore vs localStorage imported devices (completed)
  // import('./utils/fixGroupNames'); // 🔧 Fix missing group names in Firestore (completed - 26 devices fixed)
  // import('./utils/testUTU308');
  // import('./utils/testMCPDirect');
  // import('./utils/debugPower');
  // import('./utils/checkEnergyStart');
  // import('./utils/debugDeviceData');
  // import('./utils/clearCache');
  // import('./utils/checkDevice');
  // import('./utils/compareDevices');
  // import('./utils/debugSelectedDevices');
  // import('./utils/cleanSelectedDevices');
  // import('./utils/testDeviceDetailData');
  // import('./utils/test5MinuteData');
  // import('./utils/debugTodayEnergy');
  // import('./utils/showActualTodayEnergy');
  // import('./utils/calculateRealEnergy');
  // import('./utils/getAllDevicesRealData');
  // import('./utils/fetchHistoricalData');
  // import('./utils/test5MinuteCollection');
  // import('./utils/testHourlyAggregation');
  // import('./utils/quickTest');
  // import('./utils/testRealtimeUpdate');
  // import('./utils/simpleRealtimeTest');
  // import('./utils/testDirectAPI');
  // import('./utils/testSinglePowerMeter');
  // import('./utils/testPowerMeterWithApi');
  // import('./utils/testRealtimePowerMeter');
  // import('./utils/testDeviceChartData');
  // import('./utils/testGenerateReport');
  // import('./utils/testMCPReport');
  // import('./utils/testDeviceData');
  // import('./utils/testLightCommand');
  // import('./utils/debugLightingStatus');
  // import('./utils/testGardenFan');
  // import('./utils/checkGardenFan');
  // import('./utils/testAuthConflict');
  // import('./utils/testApiKeyDirect');
}

// Wrapper component for AppContent to use Router hooks
function AppRoutes() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState<boolean>(false);
  const navigate = useNavigate();

  // Listen for session-expired events
  useEffect(() => {
    const handleSessionExpired = () => {
      logger.warn('Session expired event received');
      setShowSessionExpiredModal(true);
    };

    window.addEventListener('session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, []);

  useEffect(() => {
    // 🐛 Debug: Save original URL for troubleshooting (before it gets cleared)
    const originalUrl = window.location.href;
    const originalHash = window.location.hash;
    const originalSearch = window.location.search;
    
    // Save to sessionStorage for later inspection
    if (originalHash && originalHash.includes('access_token')) {
      sessionStorage.setItem('debug_oauth_url', originalUrl);
      sessionStorage.setItem('debug_oauth_hash', originalHash);
    }
    if (originalSearch && originalSearch.includes('access_token')) {
      sessionStorage.setItem('debug_oauth_url', originalUrl);
      sessionStorage.setItem('debug_oauth_query', originalSearch);
    }
    
    console.log('🔍 [OAuth] Current URL:', originalUrl);
    console.log('🔍 [OAuth] Hash:', originalHash || '(empty)');
    console.log('🔍 [OAuth] Search:', originalSearch || '(empty)');
    
    // Extract OAuth tokens from URL hash OR query string (Token-Based OAuth 2.0)
    const hash = window.location.hash;
    const search = window.location.search;
    
    // Try hash first, then query string
    let params: URLSearchParams | null = null;
    let source = '';
    
    if (hash && hash.includes('access_token')) {
      params = new URLSearchParams(hash.substring(1));
      source = 'hash';
      console.log('🔍 [OAuth] Found tokens in hash');
    } else if (search && search.includes('access_token')) {
      params = new URLSearchParams(search.substring(1));
      source = 'query';
      console.log('🔍 [OAuth] Found tokens in query string');
    }
    
    if (params) {
      // Parse tokens from URL
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const expiresIn = params.get('expires_in');
      const environment = params.get('environment'); // 🆕 production or staging
      const apiUrl = params.get('api_url'); // 🆕 API base URL

      // 🐛 Debug: Log all parsed values
      console.log('🔍 [OAuth] Parsed values from', source, ':', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        expiresIn,
        environment,
        apiUrl
      });

      if (accessToken) {
        logger.secure('OAuth tokens received from URL ' + source);
        logger.log('🔧 OAuth environment:', environment || 'not provided (defaulting to production)');
        logger.log('🔧 OAuth API URL:', apiUrl || 'not provided');

        // ✅ Store tokens in sessionStorage (more secure, cleared on browser close)
        const session = {
          accessToken,
          refreshToken,
          expiresAt: expiresIn ? Date.now() + (parseInt(expiresIn) * 1000) : undefined,
          environment: environment || 'production', // 🆕 Save environment
          apiUrl: apiUrl || undefined // 🆕 Save API URL if provided
        };
        sessionStorage.setItem('ultron_tokens', JSON.stringify(session));

        // Mark as OAuth authenticated
        sessionStorage.setItem('auth_mode', 'oauth');

        // Initialize authService with tokens and start auto-refresh (async)
        authService.initializeFromLocalStorage().catch(err => {
          logger.error('Failed to initialize auth service:', err);
        });

        // Clear the hash/query from URL
        if (source === 'hash') {
          window.location.hash = '';
        } else if (source === 'query') {
          window.history.replaceState(null, '', window.location.pathname);
        }
        logger.secure('OAuth tokens stored and auto-refresh enabled');

        // Sync localStorage data to Firestore (first-time migration)
        // and load Firestore data to localStorage (cross-device sync)
        (async () => {
          try {
            // First, migrate any existing localStorage data to Firestore
            await migrateAllFromLocalStorage();
            logger.log('✅ localStorage data migrated to Firestore');

            // Then, load Firestore data to localStorage (for new devices)
            await loadPreferencesToLocalStorage();
            logger.log('✅ Firestore preferences loaded to localStorage');

            // Cleanup orphaned importedDevices (not in any Virtual Group)
            const cleanupResult = await cleanupOrphanedImportedDevices();
            if (cleanupResult.cleanedCount > 0) {
              logger.log(`🧹 Cleaned ${cleanupResult.cleanedCount} orphaned imported devices`);
            }

            // Migrate virtual groups from localStorage to Firestore if needed
            const { migrateVirtualGroupsIfNeeded } = await import('./services/userPreferencesApi');
            await migrateVirtualGroupsIfNeeded();
            logger.log('✅ Virtual groups migration check completed');

            // Check if Keep Logged In is enabled
            const { getUserPreferences } = await import('./services/userPreferencesApi');
            const preferences = await getUserPreferences();
            const keepLoggedIn = preferences.dashboardSettings?.keepLoggedIn || false;

            // ✅ Update authService cache to prevent circular dependency
            authService.updateKeepLoggedInCache(keepLoggedIn);

            // Always start token auto-refresh regardless of keepLoggedIn setting
            // keepLoggedIn only controls storage location (localStorage vs sessionStorage)
            logger.log('✅ Starting token auto-refresh');
            authService.setupTokenRefresh();

            // Sync WebSocket subscriptions from Firestore
            const { syncWebSocketSubscriptions } = await import('./services/deviceSubscriptionManager');
            await syncWebSocketSubscriptions();

            // ✅ One-time migration: Clean legacy deviceOrder fields
            // Only keep intelliSpace, remove energy/climate/hvac/lighting
            try {
              const { getUserPreferences, saveUserPreferences } = await import('./services/userPreferencesApi');
              const prefs = await getUserPreferences();
              if (prefs.deviceOrder && (prefs.deviceOrder.energy || prefs.deviceOrder.climate || prefs.deviceOrder.hvac || prefs.deviceOrder.lighting)) {
                logger.log('🔄 [Migration] Cleaning legacy deviceOrder fields...');
                await saveUserPreferences({
                  ...prefs,
                  deviceOrder: {
                    intelliSpace: prefs.deviceOrder.intelliSpace || []
                  }
                });
                logger.log('✅ [Migration] Legacy deviceOrder fields removed');
              }
            } catch (migrationError) {
              logger.warn('⚠️ [Migration] Failed to clean legacy deviceOrder:', migrationError);
            }

            // Full sync imported devices to webhook (Layer 1)
            // Ensures _app/config/settings.webhook_devices matches importedDevices
            // Note: cleanupWebhookSubscriptions removed - syncAllImportedDevicesToWebhook handles incremental sync
            logger.log('🔄 [App] Starting webhook sync...');
            try {
              const { ultronTuringApi } = await import('./services/ultronTuringApi');
              const syncResult = await ultronTuringApi.syncAllImportedDevicesToWebhook();
              logger.log('✅ [App] Webhook devices synced on app startup:', syncResult);
            } catch (webhookError) {
              logger.error('❌ [App] Webhook sync failed:', webhookError);
            }
          } catch (error) {
            logger.error('Failed to sync user preferences:', error);
          }
        })();
      }
    } else {
      // If no hash but have stored tokens, set auth_mode to oauth
      // Try localStorage first (for keepLoggedIn = true), then sessionStorage
      const storedTokens = localStorage.getItem('ultron_tokens') || sessionStorage.getItem('ultron_tokens');
      if (storedTokens) {
        sessionStorage.setItem('auth_mode', 'oauth');
        logger.secure('Restored OAuth auth mode from stored tokens');

        // Initialize authService with stored tokens and start auto-refresh (async)
        authService.initializeFromLocalStorage().catch(err => {
          logger.error('Failed to initialize auth service:', err);
        });

        // Load Firestore preferences to localStorage (cross-device sync)
        loadPreferencesToLocalStorage()
          .then(async () => {
            logger.log('✅ Firestore preferences loaded to localStorage');

            // Cleanup orphaned importedDevices (not in any Virtual Group)
            const { cleanupOrphanedImportedDevices } = await import('./services/userPreferencesApi');
            const cleanupResult = await cleanupOrphanedImportedDevices();
            if (cleanupResult.cleanedCount > 0) {
              logger.log(`🧹 Cleaned ${cleanupResult.cleanedCount} orphaned imported devices`);
            }

            // Migrate virtual groups from localStorage to Firestore if needed
            const { migrateVirtualGroupsIfNeeded } = await import('./services/userPreferencesApi');
            await migrateVirtualGroupsIfNeeded();
            logger.log('✅ Virtual groups migration check completed');

            // Check if Keep Logged In is enabled
            const { getUserPreferences } = await import('./services/userPreferencesApi');
            const preferences = await getUserPreferences();
            const keepLoggedIn = preferences.dashboardSettings?.keepLoggedIn || false;

            // ✅ Update authService cache to prevent circular dependency
            authService.updateKeepLoggedInCache(keepLoggedIn);

            // Always start token auto-refresh regardless of keepLoggedIn setting
            // keepLoggedIn only controls storage location (localStorage vs sessionStorage)
            logger.log('✅ Starting token auto-refresh (restored session)');
            authService.setupTokenRefresh();

            // Sync WebSocket subscriptions from Firestore
            const { syncWebSocketSubscriptions } = await import('./services/deviceSubscriptionManager');
            await syncWebSocketSubscriptions();

            // ✅ One-time migration: Clean legacy deviceOrder fields
            // Only keep intelliSpace, remove energy/climate/hvac/lighting
            try {
              const { saveUserPreferences } = await import('./services/userPreferencesApi');
              if (preferences.deviceOrder && (preferences.deviceOrder.energy || preferences.deviceOrder.climate || preferences.deviceOrder.hvac || preferences.deviceOrder.lighting)) {
                logger.log('🔄 [Migration] Cleaning legacy deviceOrder fields...');
                await saveUserPreferences({
                  ...preferences,
                  deviceOrder: {
                    intelliSpace: preferences.deviceOrder.intelliSpace || []
                  }
                });
                logger.log('✅ [Migration] Legacy deviceOrder fields removed');
              }
            } catch (migrationError) {
              logger.warn('⚠️ [Migration] Failed to clean legacy deviceOrder:', migrationError);
            }

            // Full sync imported devices to webhook (Layer 1)
            // Ensures _app/config/settings.webhook_devices matches importedDevices
            // Note: cleanupWebhookSubscriptions removed - syncAllImportedDevicesToWebhook handles incremental sync
            logger.log('🔄 [App] Starting webhook sync...');
            try {
              const { ultronTuringApi } = await import('./services/ultronTuringApi');
              const syncResult = await ultronTuringApi.syncAllImportedDevicesToWebhook();
              logger.log('✅ [App] Webhook devices synced on app startup (restored session):', syncResult);
            } catch (webhookError) {
              logger.error('❌ [App] Webhook sync failed:', webhookError);
            }
          })
          .catch((error) => {
            logger.error('Failed to load Firestore preferences:', error);
          });
      }
    }

    // Check authentication status
    const checkAuth = async () => {
      // Skip OAuth check if OAuth is not enabled
      if (authService.isOAuthEnabled()) {
        // First check OAuth session via Turing Server
        const { mcpOAuthService } = await import('./services/mcpOAuthService');
        const oauthStatus = await mcpOAuthService.checkAuthStatus();

        if (oauthStatus.authenticated) {
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        }

        // ✅ Even if OAuth check returns false, check if we have stored tokens
        // This prevents auto-logout on temporary network failures or token refresh issues
        const storedTokens = localStorage.getItem('ultron_tokens') || sessionStorage.getItem('ultron_tokens');
        if (storedTokens) {
          try {
            const tokens = JSON.parse(storedTokens);
            // If we have a refresh token, consider user as authenticated
            // The token refresh will be handled by auto-refresh mechanism
            if (tokens.refreshToken) {
              logger.log('✅ OAuth check failed but have refresh token - staying authenticated');
              setIsAuthenticated(true);
              setIsLoading(false);
              return;
            }
          } catch (e) {
            logger.error('Failed to parse stored tokens:', e);
          }
        }
      }

      // Fallback to check local auth service
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  // Apply saved favicon on app load
  useEffect(() => {
    const savedFavicon = localStorage.getItem('ultron-company-favicon');
    if (savedFavicon) {
      // Remove existing favicon links
      const existingLinks = document.querySelectorAll("link[rel*='icon']");
      existingLinks.forEach(link => link.remove());

      // Create new favicon link
      const link = document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = savedFavicon;
      document.getElementsByTagName('head')[0].appendChild(link);

      // Also set for different favicon types
      const link2 = document.createElement('link');
      link2.type = 'image/x-icon';
      link2.rel = 'icon';
      link2.href = savedFavicon;
      document.getElementsByTagName('head')[0].appendChild(link2);
    }
  }, []);

  // Initialize API credentials from localStorage on app start
  useEffect(() => {
    const storedCredentials = localStorage.getItem('ultron-energy-credentials');
    if (storedCredentials) {
      try {
        // No need to set credentials for Turing API as it uses environment variables
        logger.log('Loaded stored API credentials');
      } catch (error) {
        logger.error('Error loading stored credentials:', error);
      }
    }
  }, []);

  // Battery Alert Service (Global)
  // TODO: Battery Alert monitoring will be handled by Turing Server (24/7 background service)
  // Settings page will provide UI to configure alerts via ConfigureAlert API
  // Front-end only receives notifications via WebSocket
  useEffect(() => {
    logger.log('🔋 Battery Alert: Monitoring handled by Turing Server');
    // Battery Alert configuration and monitoring is now server-side
    // When user configures alerts in Settings, it will call ultronTuringApi.configureAlert()
    // Notifications will be received via WebSocket
  }, []);

  // Protected route component
  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      );
    }

    // Check for stored credentials (API key mode)
    const storedCredentials = localStorage.getItem('ultron-energy-credentials');
    const hasApiCredentials = storedCredentials && JSON.parse(storedCredentials).apiKey;

    // Check auth mode from session storage
    const authMode = sessionStorage.getItem('auth_mode');
    const isApiKeyAuth = authMode === 'apikey';

    // Allow access if:
    // 1. OAuth authenticated, OR
    // 2. API key credentials exist and auth_mode is set to 'apikey'
    const hasValidAuth = isAuthenticated || (hasApiCredentials && isApiKeyAuth);

    if (!hasValidAuth) {
      return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
  };


  // No OAuth callback handler needed - Turing Server handles everything!
  // When user returns from OAuth, they'll be redirected directly to the return_url
  // with session cookie already set

  const handleRelogin = () => {
    setShowSessionExpiredModal(false);
    // Clear tokens from both localStorage and sessionStorage
    sessionStorage.removeItem('ultron_tokens');
    sessionStorage.removeItem('auth_mode');
    sessionStorage.removeItem('auth_tokens');
    sessionStorage.removeItem('user_profile');

    localStorage.removeItem('ultron_tokens');
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('user_profile');

    logger.log('🔐 Session expired, cleared all tokens and redirecting to login');
    window.location.href = '/login';
  };

  const handleDismissModal = () => {
    setShowSessionExpiredModal(false);
  };

  return (
    <div className="App">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} /> {/* Keep for backwards compatibility */}
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings onBack={() => navigate(-1)} />
          </ProtectedRoute>
        } />
        <Route path="/" element={
          <ProtectedRoute>
            <IntelliSpaceDashboard onSettingsClick={() => navigate('/settings')} />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Session Expired Modal */}
      {showSessionExpiredModal && (
        <SessionExpiredModal
          onRelogin={handleRelogin}
          onDismiss={handleDismissModal}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <LanguageProvider>
        <ThemeProvider>
          <AlertProvider>
            <AppModeProvider>
              <AppRoutes />
            </AppModeProvider>
          </AlertProvider>
        </ThemeProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;
